import React, { useEffect, useState } from "react";
import { MainContainer, Section } from "./styles";
import Typography from "../../../../components/Typography";
import SectionItem from "../../../../components/SectionItem";
import { Divider } from "antd";
import useEnterprise from "../../hooks/useEnterprise";
import { BrandPersonality as BrandPersonalityType } from "./types";
import { brandPersonalityService } from "../../../../services/modules/brandPersonality";
import WaitingMessage from "../../../../components/WaitingMessage";
import { SkeletonLoading } from "../skeleton";
import Facebook from "../../../../assets/png/facebook.png";
import Instagram from "../../../../assets/png/instagram.png";
import TikTok from "../../../../assets/png/tiktok.png";
import Linkedin from "../../../../assets/png/linkedin.png";
import Twitter from "../../../../assets/png/twitter.png";

const BrandPersonality: React.FC = (): JSX.Element => {
  const { enterpriseState } = useEnterprise();

  const [brandData, setBrandData] = useState<BrandPersonalityType>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchBriefing = (id: string) => {
    brandPersonalityService
      .getBrandPersonality(id)
      .then((response) => setBrandData(response))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (enterpriseState.selectedEnterprise)
      fetchBriefing(enterpriseState.selectedEnterprise);
  }, [enterpriseState.selectedEnterprise]);

  const mapPlatform: {
    [key: string]: { label: string; icon: React.ReactNode };
  } = {
    tik_tok: { label: "Tik Tok", icon: <img src={TikTok} width={18} /> },
    facebook: { label: "Facebook", icon: <img src={Facebook} width={18} /> },
    x: { label: "X", icon: <img src={Twitter} width={18} /> },
    instagram: { label: "Instagram", icon: <img src={Instagram} width={18} /> },
    linkedin: { label: "LinkedIn", icon: <img src={Linkedin} width={18} /> },
  };

  return (
    <Section>
      {isLoading ? (
        <SkeletonLoading />
      ) : (
        <>
          {brandData ? (
            <MainContainer>
              <Typography
                text={enterpriseState.enterpriseData?.company_name}
                size="extraHuge"
                color="Black500"
                weight={700}
              />
              <Divider />
              <SectionItem
                icon={"🎯"}
                title={"PERSONALIDADE DA MARCA"}
                description={"Atributos que descrevem a essência da marca."}
                isTitle
              />
              <SectionItem
                icon={"🙂"}
                title={"Tom Geral"}
                description={brandData.brand_personality.general_tone}
              />
              <SectionItem
                icon={"🏆"}
                title={"Valores Principais"}
                description={brandData.brand_personality.principal_values.join(
                  ", "
                )}
              />
              <Divider />
              <SectionItem
                icon={"🎯"}
                title={"ESTRATÉGIA POR CANAIS DE COMUNICAÇÃO"}
                description={
                  "Plataformas prioritárias para a comunicação da marca."
                }
                isTitle
              />
              {Object.keys(brandData.communication_channels_strategies).map(
                (platform, index) => {
                  if (
                    platform !== "_id" &&
                    brandData.communication_channels_strategies[
                      platform as keyof unknown
                    ] !== null
                  )
                    return (
                      <SectionItem
                        key={index}
                        icon={mapPlatform[platform].icon}
                        title={mapPlatform[platform].label}
                        description={
                          brandData.communication_channels_strategies[
                            platform as keyof unknown
                          ]
                        }
                      />
                    );
                }
              )}

              <Divider />
              <SectionItem
                icon={"🎯"}
                title={"ESTILO DE LINGUAGEM"}
                description={
                  "Diretrizes sobre o estilo e a linguagem a serem usadas."
                }
                isTitle
              />
              <SectionItem
                icon={"📜"}
                title={"Formalidade"}
                description={brandData.language_style.formality}
              />
              <SectionItem
                icon={"🔠"}
                title={"Estilo de Escrita"}
                description={brandData.language_style.writing_style}
              />
              <Divider />
              <SectionItem
                icon={"🎯"}
                title={"EMOÇÕES A SEREM EVOCADAS"}
                description={
                  "Sentimentos que a marca deseja inspirar em seu público."
                }
                isTitle
              />
              <SectionItem
                icon={"❤️"}
                title={"Emoção Principal"}
                description={brandData.emotions_to_evoke.principal_emotion}
              />
              <SectionItem
                icon={"🔔"}
                title={"Emoção Secundária"}
                description={brandData.emotions_to_evoke.secondary_emotion}
              />
              <Divider />
              <SectionItem
                icon={"🎯"}
                title={"DIRETRIZES ESPECÍFICAS"}
                description={
                  "Regras e considerações adicionais para garantir a consistência da comunicação."
                }
                isTitle
              />
              <SectionItem
                icon={"📏"}
                title={"Regras de Formatação e Estrutura Textual"}
                description={
                  brandData.specific_guidelines
                    .formatting_and_text_structure_rules
                }
              />
              <SectionItem
                icon={"🚫"}
                title={"Palavras ou Frases Proibidas"}
                description={brandData.specific_guidelines.prohibited_words_or_phrases.join(
                  ", "
                )}
              />
              <Divider />
              <SectionItem
                icon={"🎯"}
                title={"HASHTAGS E EMOJIS"}
                description={
                  "Sugestões para aumentar o envolvimento e a identidade digital."
                }
                isTitle
              />
              <SectionItem
                icon={"🏷️"}
                title={"Hashtags Principais"}
                description={brandData.hashtags_and_emojis.principal_hashtags.join(
                  ", "
                )}
              />
              <SectionItem
                icon={"😊"}
                title={"Emojis da Marca"}
                description={brandData.hashtags_and_emojis.brand_emojis.join(
                  " "
                )}
              />
            </MainContainer>
          ) : (
            <WaitingMessage />
          )}
        </>
      )}
    </Section>
  );
};

export default BrandPersonality;
